import React, { useEffect, useRef, useState } from 'react';
import Request from 'components/Request';
import { __ } from 'i18n/index';
import { __noop } from 'i18n/noop';
import { openCurrentMatch } from 'react/dock/matchDockPlacement';
import askForConnectionType, { needsConnectionType } from 'react/matchmaking/connection/askForConnectionType';
import { searchOptionsForMode, FRIENDLIES, RANKED } from 'react/matchmaking/findMatch/findMatchModes';

// The Play page's Friendlies and Ranked buttons, with the settings the player
// saved for each (my_user.mode_settings, in the site's store once startup has
// run; the defaults until then). Ranked is always a best of 3 found
// automatically.
const MODES = {
	[FRIENDLIES]: { label: __noop('Friendlies') },
	[RANKED]: { label: __noop('Ranked') },
};

// Browser only: the store stays out of the server render (docs/ssr.md).
function loadModeSettings() {
	return import('react/redux/store/store')
		.then((storeModule) => storeModule.default.getState().findMatch.modeSettings)
		.catch(() => null);
}

function startParameters(mode, modeSettings, ladderId) {
	const options = searchOptionsForMode(mode, modeSettings, ladderId);
	return {
		ladder_id: options.ladderId,
		match_count: options.gameCount,
		ranked: options.isRanked ? 1 : 0,
		team_size: options.teamSize,
		auto_queue_setting: options.queueVisibilityType,
	};
}

/**
 * "Play <game> now": pick a game and a mode, then one button. The game menu
 * lists the games BroadcastHub offers (a member's active games); a member can
 * also edit those games from it (onEditGames). Members start a
 * real search and land on /netplay, which owns searching and the match itself.
 * A member already in a match gets a link back to it instead, which opens the
 * match in place (the home match column, or the match dock's sheet) when the
 * page can show it and goes to /netplay otherwise, and one whose
 * match started after the page loaded is sent there when the server refuses
 * the search. Guests are sent to sign up.
 */
// A plain click opens the match on this page; a modified or middle click is
// the browser's, to open /netplay in a new tab.
function returnToMatch(event) {
	if(event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
		return;
	}
	if(openCurrentMatch()) {
		event.preventDefault();
	}
}

export default function PlayBar({ games, ladderId, onLadderChange, onEditGames = null, searchingCount, isLoggedIn, currentMatch, siteUrl, navigate }) {
	const [mode, setMode] = useState(FRIENDLIES);
	const [isStarting, setIsStarting] = useState(false);
	const [error, setError] = useState(null);
	const game = games.find((candidate) => candidate.ladder_id === ladderId) || games[0];

	const startSearch = () => {
		setIsStarting(true);
		setError(null);
		loadModeSettings()
			.then((modeSettings) => Request.apiv1('matchmaking/start', startParameters(mode, modeSettings, game.ladder_id)))
			.then(() => navigate(siteUrl + '/netplay'))
			.catch((failure) => {
				const response = (failure && failure.responseJSON) || {};
				if(response.error_data && response.error_data.in_match) {
					navigate(siteUrl + '/netplay');
					return;
				}
				if(needsConnectionType(response)) {
					askForConnectionType().then((connectionType) => (connectionType ? startSearch() : setIsStarting(false)));
					return;
				}
				const message = response.error;
				setError(message || __('Could not start a search. Try again from the Play page.'));
				setIsStarting(false);
			});
	};

	return (
		<div className='play-bar'>
			<div className='play-bar__game'>
				<img src={game.icon_url} alt='' className='play-bar__logo' />
				<div className='play-bar__game-text'>
					<span className='play-bar__eyebrow'>{__('Game')}</span>
					<h2 className='play-bar__title'>{__('Play %s', game.name)}</h2>
					<GameMenu games={games} game={game} onLadderChange={onLadderChange} onEditGames={onEditGames} />
				</div>
			</div>

			<div className='play-bar__modes' role='radiogroup' aria-label={__('Mode')}>
				{Object.keys(MODES).map((modeKey) => (
					<button
						key={modeKey}
						type='button'
						role='radio'
						aria-checked={mode === modeKey}
						className={'play-bar__mode' + (mode === modeKey ? ' is-active' : '')}
						onClick={() => setMode(modeKey)}
					>
						{__(MODES[modeKey].label)}
					</button>
				))}
			</div>

			<div className='play-bar__activity'>
				{searchingCount > 0
					? <span className='play-bar__searching'>{__('%s searching', searchingCount)}</span>
					: <span className='play-bar__muted'>{__('Nobody is searching %s yet. Start a search and others will see you.', game.name)}</span>}
			</div>

			{isLoggedIn && currentMatch && (
				<a className='play-bar__go' href={siteUrl + '/netplay'} onClick={returnToMatch}>
					{currentMatch.opponent_name
						? __('Return to your match vs %s', currentMatch.opponent_name)
						: __('Return to your match')}
				</a>
			)}
			{isLoggedIn && !currentMatch && (
				<button type='button' className='play-bar__go' onClick={startSearch} disabled={isStarting}>
					{isStarting ? __('Starting…') : __('Find a match')}
				</button>
			)}
			{!isLoggedIn && <a className='play-bar__go' href={siteUrl + '/sign-up'}>{__('Find a match')}</a>}

			{error && <div className='play-bar__error' role='alert'>{error}</div>}
		</div>
	);
}

function GameMenu({ games, game, onLadderChange, onEditGames }) {
	const [open, setOpen] = useState(false);
	const menuRef = useRef(null);
	const buttonRef = useRef(null);

	useEffect(() => {
		if(!open) {
			return undefined;
		}
		const closeOnOutsideClick = (event) => {
			if(menuRef.current && !menuRef.current.contains(event.target)) {
				setOpen(false);
			}
		};
		document.addEventListener('mousedown', closeOnOutsideClick);
		return () => document.removeEventListener('mousedown', closeOnOutsideClick);
	}, [open]);

	const close = () => {
		setOpen(false);
		buttonRef.current.focus();
	};

	const choose = (ladderId) => {
		onLadderChange(ladderId);
		close();
	};

	const editGames = () => {
		setOpen(false);
		onEditGames();
	};

	return (
		<div className='play-bar__game-menu' ref={menuRef}>
			<button
				ref={buttonRef}
				type='button'
				className='play-bar__picker'
				aria-haspopup='menu'
				aria-expanded={open}
				aria-label={__('Choose a game, %s selected', game.name)}
				onClick={() => setOpen(!open)}
			>
				{game.name}
				<i className='fa fa-chevron-down' aria-hidden='true' />
			</button>
			{open && (
				<div
					className='play-bar__menu'
					role='menu'
					aria-label={__('Games')}
					onKeyDown={(event) => event.key === 'Escape' && close()}
				>
					{games.map((option) => (
						<button
							key={option.ladder_id}
							type='button'
							role='menuitemradio'
							aria-checked={option.ladder_id === game.ladder_id}
							className={'play-bar__menu-item' + (option.ladder_id === game.ladder_id ? ' is-active' : '')}
							onClick={() => choose(option.ladder_id)}
						>
							<img src={option.icon_url} alt='' className='play-bar__menu-icon' />
							{option.name}
						</button>
					))}
					{onEditGames && (
						<button type='button' role='menuitem' className='play-bar__menu-item play-bar__menu-item--edit' onClick={editGames}>
							<i className='fa fa-pencil' aria-hidden='true' />
							{__('Edit games')}
						</button>
					)}
				</div>
			)}
		</div>
	);
}
